import React, { useEffect, useId, useRef, useState } from 'react';

interface CopilotReasoningProps {
  /** Accumulated reasoning ("thinking") text streamed from the agent. */
  reasoning: string;
  /**
   * True while the model is actively thinking — i.e. the turn is streaming and the
   * final answer has not started yet. Drives the live, auto-expanded, auto-scrolling
   * "Thinking…" box; once it flips false (the answer begins or the turn ends) the
   * trace collapses into a re-openable "Thinking completed" toggle.
   */
  active: boolean;
}

/**
 * Collapsible model-reasoning trace shown above an agent answer, mirroring how
 * standard assistant UIs surface thinking: a fixed-height, auto-scrolling live box
 * while the model thinks, then collapsed away once the final answer is displayed.
 *
 * A small custom disclosure (button + `+`/`−` glyph sized to the label) is used
 * instead of Cloudscape's ExpandableSection so the toggle icon can be a text-sized
 * `+` — the ExpandableSection chevron's glyph and size are not configurable via its
 * public API. Rendered by the caller only when reasoning text exists, so stages with
 * extended thinking disabled never show it.
 */
export function CopilotReasoning({ reasoning, active }: CopilotReasoningProps) {
  const [expanded, setExpanded] = useState(active);
  const bodyRef = useRef<HTMLDivElement>(null);
  const bodyId = useId();

  // Follow the thinking phase: expand while active, collapse once the answer starts.
  useEffect(() => {
    setExpanded(active);
  }, [active]);

  // Keep the newest reasoning line in view while thinking (fixed-height scroll box).
  useEffect(() => {
    if (active && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [reasoning, active]);

  return (
    <div className="copilot-reasoning-wrapper">
      <button
        type="button"
        className="copilot-reasoning-toggle"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span className="copilot-reasoning-icon" aria-hidden="true">
          {expanded ? '−' : '+'}
        </span>
        <span className="copilot-reasoning-label">
          {active ? 'Thinking…' : 'Thinking completed'}
        </span>
      </button>
      {expanded && (
        <div id={bodyId} ref={bodyRef} className="copilot-reasoning">
          {reasoning}
        </div>
      )}
    </div>
  );
}
